import { useEffect, useState } from 'react';

import { StatusDropdown } from '@/components/ui/status-dropdown';
import { useUpdateOrderSourceStatus } from '@hooks/orderSources';
import { useTranslation } from '@hooks/useTranslation';
import { ConfirmationDialog } from '../../common/ConfirmationDialog';

import {
  createOrderSourceStatusDropdownConfig,
  type OrderSourceStatus,
} from '../order-source-status-config';

type Status = OrderSourceStatus;

interface OrderSourceStatusDropdownProps {
  orderSourceId: number;
  active: boolean;
}

export function OrderSourceStatusDropdown({
  orderSourceId,
  active,
}: OrderSourceStatusDropdownProps) {
  const { t } = useTranslation();
  const [currentActive, setCurrentActive] = useState(active);
  const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false);
  const updateStatus = useUpdateOrderSourceStatus();

  // Keep local UI state in sync if the prop changes (e.g. parent refetch).
  useEffect(() => {
    setCurrentActive(active);
  }, [active]);

  const config = createOrderSourceStatusDropdownConfig({
    active: t('common.status.active'),
    inactive: t('common.status.inactive'),
  });
  const value: Status = currentActive ? 'active' : 'inactive';

  const handleChange = (next: Status) => {
    const nextActive = next === 'active';
    if (nextActive === currentActive) return;
    // Activate fires immediately (no confirm). Guard against rapid re-selection
    // before isPending propagates to the trigger's disabled state.
    if (updateStatus.isPending) return;

    if (!nextActive) {
      setConfirmDeactivateOpen(true);
      return;
    }

    updateStatus.mutate(
      { id: orderSourceId, active: true },
      { onSuccess: () => setCurrentActive(true) },
    );
  };

  const handleConfirmDeactivate = () => {
    setConfirmDeactivateOpen(false);
    updateStatus.mutate(
      { id: orderSourceId, active: false },
      { onSuccess: () => setCurrentActive(false) },
    );
  };

  return (
    <>
      <StatusDropdown
        value={value}
        config={config}
        onChange={handleChange}
        disabled={updateStatus.isPending}
      />
      <ConfirmationDialog
        open={confirmDeactivateOpen}
        onOpenChange={setConfirmDeactivateOpen}
        onConfirm={handleConfirmDeactivate}
        title={t('status_dropdown.deactivate_confirm_title')}
        // Widen past the default 239px so the message wraps to ~2 lines instead of 4.
        messageClassName="max-w-none"
        message={t('status_dropdown.deactivate_confirm_message')}
        confirmLabel={t('status_dropdown.deactivate_confirm_action')}
        cancelLabel={t('status_dropdown.deactivate_cancel_action')}
        variant="destructive"
      />
    </>
  );
}
